import { reactive } from 'vue'
import { ElMessage } from 'element-plus'
import ApiService from '@/core/services/ApiService'
import { endpoints } from '@/core/services/Endpoints'
import i18n from '@/core/plugins/vue-i18n'
import eventBus from '@/core/plugins/event-bus'

const state = reactive({
    loading: false,
    saving: false,
    transactionId: null,
    transaction: null,
    sourceContext: null,
    paymentMethods: [],
    cashRegisters: [],
    error: null,
    paymentMethodsLoaded: false,
    cashRegistersLoaded: false,

    activeForm: null,
    editingPaymentId: null,
    formAmount: '',
    formMethodId: null,
    formCashRegisterId: null,
    formPaidAtNow: true,
    formDueDate: '',
    formNote: '',
    markPaidPopoverId: null,
    markPaidCashRegisterId: null,

    installmentCount: 6,
    installmentStartDate: '',

    onChange: null,
    onClose: null,
    changed: false,
})

function t(key) {
    const text = i18n.global.t(key)
    return text === key ? null : text
}

function trError(code) {
    if (!code) return t('common.transaction.errors.generic')
    return t(`common.transaction.errors.${code}`) || code
}

function showError(err) {
    const code = err?.response?.data?.message
    const text = trError(code)
    if (text) ElMessage.error(text)
}

function nowDateTime() {
    const d = new Date()
    const pad = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function dateOnly(value) {
    if (!value) return null
    return String(value).slice(0, 10)
}

function defaultCashRegisterId() {
    const def = state.cashRegisters.find(c => c.is_default)
    return def ? def.id : (state.cashRegisters[0]?.id ?? null)
}

function allocatedAmount(excludePaymentId = null) {
    const payments = state.transaction?.payments
    if (!Array.isArray(payments)) return 0
    return payments.reduce((sum, p) => {
        if (p.status_id === 2 || p.status_id === 3) return sum
        if (excludePaymentId && p.id === excludePaymentId) return sum
        return sum + (parseFloat(p.amount) || 0)
    }, 0)
}

function allocatableAmount(excludePaymentId = null) {
    if (!state.transaction) return 0
    const total = parseFloat(state.transaction.total_amount) || 0
    return Math.max(0, total - allocatedAmount(excludePaymentId))
}

let _paymentMethodsPromise = null
let _cashRegistersPromise = null

function loadPaymentMethods(force = false) {
    if (state.paymentMethodsLoaded && !force) return Promise.resolve()
    if (_paymentMethodsPromise && !force) return _paymentMethodsPromise
    _paymentMethodsPromise = ApiService.axios.get(endpoints.payment_method, {
        params: { scope: 'COMPANY_TRANSACTION', page_limit: 100, active: 'true' },
    }).then(res => {
        state.paymentMethods = (res.data?.data || []).filter(m => m.active !== false)
        state.paymentMethodsLoaded = true
    }).catch(err => {
        showError(err)
    }).finally(() => {
        _paymentMethodsPromise = null
    })
    return _paymentMethodsPromise
}

function loadCashRegisters(force = false) {
    if (state.cashRegistersLoaded && !force) return Promise.resolve()
    if (_cashRegistersPromise && !force) return _cashRegistersPromise
    _cashRegistersPromise = ApiService.axios.get(endpoints.company_cash, {
        params: { page_limit: 100 },
    }).then(res => {
        state.cashRegisters = res.data?.data || []
        state.cashRegistersLoaded = true
    }).catch(err => {
        showError(err)
    }).finally(() => {
        _cashRegistersPromise = null
    })
    return _cashRegistersPromise
}

const service = {
    state,

    methodById(id) { return state.paymentMethods.find(m => m.id === id) || null },
    cashRegisterById(id) { return state.cashRegisters.find(c => c.id === id) || null },

    getAllocatedAmount(excludePaymentId = null) { return allocatedAmount(excludePaymentId) },
    getAllocatableAmount(excludePaymentId = null) { return allocatableAmount(excludePaymentId) },

    async loadByTransactionId(transactionId) {
        if (!transactionId) return
        if (state.transactionId && state.transactionId !== transactionId) {
            this.resetForm()
        }
        state.loading = true
        state.error = null
        state.transactionId = transactionId
        try {
            await Promise.all([loadPaymentMethods(), loadCashRegisters()])
            const res = await ApiService.axios.get(`${endpoints.company_transaction}/${transactionId}`)
            state.transaction = res.data?.data || null
            if (!state.transaction) state.error = 'record_not_found'
        } catch (err) {
            state.error = err?.response?.data?.message || 'generic'
            state.transaction = null
            showError(err)
        } finally {
            state.loading = false
        }
    },

    async loadBySource({ sourceType, sourceId }) {
        if (!sourceType || !sourceId) return
        state.loading = true
        state.error = null
        try {
            await Promise.all([loadPaymentMethods(), loadCashRegisters()])
            const res = await ApiService.axios.get(endpoints.company_transaction, {
                params: { source_type: sourceType, source_id: sourceId, page_limit: 1, sort: 'id:desc' },
            })
            const found = (res.data?.data || [])[0]
            if (found) {
                await this.loadByTransactionId(found.id)
            } else {
                state.error = 'record_not_found'
                state.transaction = null
                state.transactionId = null
                state.loading = false
            }
        } catch (err) {
            state.error = err?.response?.data?.message || 'generic'
            state.transaction = null
            state.transactionId = null
            state.loading = false
            showError(err)
        }
    },

    open(context = {}) {
        state.sourceContext = context.sourceContext || null
        state.onChange = typeof context.onChange === 'function' ? context.onChange : null
        state.onClose = typeof context.onClose === 'function' ? context.onClose : null
        state.changed = false
        if (context.transactionId) {
            this.loadByTransactionId(context.transactionId)
        } else if (context.sourceType && context.sourceId) {
            this.loadBySource({ sourceType: context.sourceType, sourceId: context.sourceId })
        }
        eventBus.$emit('panel:open', { panelId: 'paymentPanel' })
    },

    notifyChange(event) {
        state.changed = true
        if (typeof state.onChange === 'function') {
            try { state.onChange(event) } catch (_) { /* empty */ }
        }
    },

    close() {
        const closeHandler = state.onClose
        const wasChanged = state.changed
        const transaction = state.transaction
        this.resetForm()
        state.transactionId = null
        state.transaction = null
        state.error = null
        state.onChange = null
        state.onClose = null
        state.changed = false
        if (typeof closeHandler === 'function') {
            try { closeHandler({ changed: wasChanged, transaction }) } catch (_) { /* empty */ }
        }
    },

    resetForm() {
        state.activeForm = null
        state.editingPaymentId = null
        state.formAmount = ''
        state.formMethodId = null
        state.formCashRegisterId = null
        state.formPaidAtNow = true
        state.formDueDate = ''
        state.formNote = ''
        state.installmentCount = 6
        state.installmentStartDate = ''
        state.markPaidPopoverId = null
        state.markPaidCashRegisterId = null
    },

    openAddPaymentForm() {
        this.resetForm()
        state.activeForm = 'payment'
        const slot = allocatableAmount()
        state.formAmount = slot > 0 ? slot.toFixed(2) : ''
        state.formMethodId = state.paymentMethods[0]?.id ?? null
        state.formCashRegisterId = defaultCashRegisterId()
        state.formPaidAtNow = true
    },

    openEditPaymentForm(paymentId) {
        const payment = state.transaction?.payments?.find(p => p.id === paymentId)
        if (!payment) return
        this.resetForm()
        state.activeForm = 'payment'
        state.editingPaymentId = paymentId
        state.formAmount = payment.amount
        state.formMethodId = payment.payment_method_id
        state.formCashRegisterId = payment.cash_register_id ?? defaultCashRegisterId()
        state.formPaidAtNow = !!payment.paid_at
        state.formDueDate = payment.due_date || ''
        state.formNote = payment.note || ''
    },

    openInstallmentForm() {
        this.resetForm()
        state.activeForm = 'installment'
        state.installmentCount = 6
        state.installmentStartDate = ''
    },

    closeForm() {
        this.resetForm()
    },

    async submitPaymentForm() {
        if (!state.transaction) return
        const amount = parseFloat(state.formAmount)
        if (!(amount > 0) || !state.formMethodId) return

        const body = {
            transaction_id: state.transaction.id,
            payment_method_id: state.formMethodId,
            amount,
        }
        if (state.formNote) body.note = state.formNote
        if (state.formPaidAtNow) {
            if (state.formCashRegisterId) body.cash_register_id = state.formCashRegisterId
            body.paid_at = nowDateTime()
        } else if (state.formDueDate) {
            body.due_date = dateOnly(state.formDueDate)
        }

        state.saving = true
        const isEdit = !!state.editingPaymentId
        try {
            if (isEdit) {
                await ApiService.axios.put(`${endpoints.company_transaction_payment}/${state.editingPaymentId}`, body)
            } else {
                await ApiService.axios.post(endpoints.company_transaction_payment, body)
            }
            this.resetForm()
            await this.loadByTransactionId(state.transaction.id)
            this.notifyChange({ type: isEdit ? 'payment_updated' : 'payment_added', transaction: state.transaction })
        } catch (err) {
            showError(err)
        } finally {
            state.saving = false
        }
    },

    openMarkPaidPopover(paymentId) {
        state.markPaidPopoverId = paymentId
        state.markPaidCashRegisterId = defaultCashRegisterId()
    },

    closeMarkPaidPopover() {
        state.markPaidPopoverId = null
        state.markPaidCashRegisterId = null
    },

    async confirmMarkPaid() {
        const paymentId = state.markPaidPopoverId
        if (!paymentId || !state.transaction) return
        state.saving = true
        try {
            const url = endpoints.company_transaction_payment_mark_paid.replace(':paymentId', paymentId)
            const body = {}
            if (state.markPaidCashRegisterId) body.cash_register_id = state.markPaidCashRegisterId
            await ApiService.axios.patch(url, body)
            this.closeMarkPaidPopover()
            await this.loadByTransactionId(state.transaction.id)
            this.notifyChange({ type: 'payment_marked_paid', paymentId, transaction: state.transaction })
        } catch (err) {
            showError(err)
        } finally {
            state.saving = false
        }
    },

    async deletePayment(paymentId) {
        if (!state.transaction) return
        state.saving = true
        try {
            await ApiService.axios.delete(`${endpoints.company_transaction_payment}/${paymentId}`)
            await this.loadByTransactionId(state.transaction.id)
            this.notifyChange({ type: 'payment_deleted', paymentId, transaction: state.transaction })
        } catch (err) {
            showError(err)
        } finally {
            state.saving = false
        }
    },

    async createInstallmentPlan() {
        if (!state.transaction) return
        if (state.transaction.direction_id !== 1) return
        const count = parseInt(state.installmentCount, 10)
        if (!(count >= 2 && count <= 48)) return

        state.saving = true
        try {
            const body = { installment_count: count }
            if (state.installmentStartDate) body.start_date = dateOnly(state.installmentStartDate)
            const url = endpoints.company_transaction_installment.replace(':transactionId', state.transaction.id)
            await ApiService.axios.post(url, body)
            this.resetForm()
            await this.loadByTransactionId(state.transaction.id)
            this.notifyChange({ type: 'installment_plan_created', transaction: state.transaction })
        } catch (err) {
            showError(err)
        } finally {
            state.saving = false
        }
    },

    ensurePaymentMethods() { return loadPaymentMethods(false) },
    ensureCashRegisters() { return loadCashRegisters(false) },
    refreshCashRegisters() { return loadCashRegisters(true) },
    refreshPaymentMethods() { return loadPaymentMethods(true) },
}

export default service
